import { DefaultTable } from 'Components/common/DefaultTable';
import { Main as Container } from 'Components/Main';
import { getTableHeader, getDetailColumn } from '../table-header.js';
import { AuditLogDetailDialog } from '../AuditLogDetailDialog.jsx';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { Controller } from 'react-hook-form';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { Button, PageHeader, Select, TextField } from 'Components/ui';
import { useAuditLogQuery } from '../useAuditLogQuery.js';
import { SearchSvg } from '../SearchSvg.jsx';
import * as S from '../styles.js';

// VO-only, cross-tenant Config Global audit log — the counterpart to Tenant/index.jsx.
// Reachability is enforced at the route level (routing.js's onlyAdminVO on
// /audit-logs/global), not by any role check in this component.
//
// The Company filter is a single-company switcher (see useAuditLogQuery's targetCompanyKey):
// one request always resolves to exactly one company's logs, never an aggregate of several.
// It defaults to the viewer's own company (never blank) so every row in a given page load
// already has one obvious company — the "Empresa" column below reports that value per row
// from the real API field (companyKey), matching today's real single-company-at-a-time
// semantics rather than implying a multi-tenant aggregate that doesn't exist.
export const AuditLogsGlobal = () => {
  const {
    t,
    companyTimezone,
    companyLocale,
    allCompanies,
    refetch,
    setRefetch,
    page,
    setPage,
    isExporting,
    selectedLog,
    setSelectedLog,
    register,
    control,
    reset,
    isLoading,
    error,
    pagedLogs,
    handleExportCsv,
  } = useAuditLogQuery({ withCompanyFilter: true });

  const companyNameByKey = (companyKey) => (allCompanies ?? []).find((c) => c.slug === companyKey)?.name ?? companyKey ?? '';

  const tableHeader = getTableHeader(t, companyTimezone, companyLocale, [
    {
      id: 'company',
      type: 'text',
      value: 'Empresa',
      render: (_value, item) => companyNameByKey(item.companyKey),
    },
    getDetailColumn(t, setSelectedLog),
  ]);

  return (
    <Container>
      <PageHeader
        title={t('audit:page.title')}
        segments={[
          { label: t('common:breadcrumbs.generalSettings') },
          { label: t('audit:page.title') },
        ]}
        actions={
          <Button variant="outline" onClick={handleExportCsv} disabled={isExporting}>
            {isExporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
        }
      />
      {error && <div>Ocorreu um erro ...</div>}
      {!error && (
        <>
          <S.FilterCard>
            <S.FilterCardLabel>{t('audit:page.filters.label')}</S.FilterCardLabel>
            <S.FilterRow $cols={4}>
              <S.FilterField>
                <S.FilterFieldLabel id="targetCompanyKey-label">{t('audit:page.filters.company')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="targetCompanyKey"
                  render={({ field }) => (
                    <Select
                      name="targetCompanyKey"
                      value={field.value}
                      onChange={field.onChange}
                      options={(allCompanies ?? []).map((company) => ({ value: company.slug, label: company.name }))}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel htmlFor="audit-filter-startDate">{t('audit:page.filters.startDate')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <TextField
                      id="audit-filter-startDate"
                      type="date"
                      fullWidth
                      {...field}
                      value={field.value || ''}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel htmlFor="audit-filter-endDate">{t('audit:page.filters.endDate')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <TextField
                      id="audit-filter-endDate"
                      type="date"
                      fullWidth
                      {...field}
                      value={field.value || ''}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel id="service-label">{t('audit:page.filters.service')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="service"
                  render={({ field }) => (
                    <Select
                      name="service"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: '', label: 'Todos os serviços' },
                        { value: 'user', label: 'user' },
                        { value: 'ga', label: 'ga' },
                        { value: 'mapping', label: 'mapping' },
                        { value: 'policy', label: 'policy' },
                        { value: 'vendor', label: 'vendor' },
                        { value: 'company', label: 'company' },
                        { value: 'cspm', label: 'cspm' },
                        { value: 'tc', label: 'tc' },
                        { value: 'asm', label: 'asm' },
                        { value: 'cookie', label: 'cookie' },
                        { value: 'mcp', label: 'mcp' },
                      ]}
                    />
                  )}
                />
              </S.FilterField>
            </S.FilterRow>
            <S.FilterRow $cols={4} $last>
              <S.FilterField>
                <S.FilterFieldLabel id="typeFilter-label">{t('audit:page.filters.filterBy')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="typeFilter"
                  render={({ field }) => (
                    <Select
                      name="typeFilter"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: '', label: 'Selecione o campo' },
                        ...['Usuário', 'Ação', 'Resposta'].map((item) => ({ value: item, label: item })),
                      ]}
                    />
                  )}
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel htmlFor="audit-filter-searchContent">{t('audit:page.filters.search')}</S.FilterFieldLabel>
                <TextField
                  id="audit-filter-searchContent"
                  {...register('searchContent')}
                  fullWidth
                />
              </S.FilterField>
              <S.FilterField>
                <S.FilterFieldLabel id="actorType-label">{t('audit:page.filters.actorType')}</S.FilterFieldLabel>
                <Controller
                  control={control}
                  name="actorType"
                  render={({ field }) => (
                    <Select
                      name="actorType"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: '', label: 'Todos os tipos' },
                        { value: 'user', label: 'Usuário' },
                        { value: 'system', label: 'Sistema' },
                        { value: 'external', label: 'Externo' },
                        { value: 'ai_agent', label: 'Agente de IA' },
                      ]}
                    />
                  )}
                />
              </S.FilterField>
            </S.FilterRow>
            <S.FilterActions>
              <Button variant="secondary" size="sm" type="button" sx={S.FilterButtonSx}
                onClick={() => { reset(); setRefetch(!refetch); }}>
                {t('common:buttons.clear')}
              </Button>
              <Button variant="primary" size="sm" type="button" sx={S.FilterButtonSx} startIcon={<SearchSvg />}
                onClick={() => { setRefetch(!refetch); }}>
                {t('common:buttons.filter')}
              </Button>
            </S.FilterActions>
          </S.FilterCard>
          {!isLoading && pagedLogs.data.length === 0 && <NoData description={'Não há logs para exibir'} />}
          {!isLoading && pagedLogs.data?.length > 0 && (
            <div className="container mt-3">
              <DefaultTable
                rows={tableHeader}
                cells={pagedLogs.data}
                onClickDelete={() => {}}
                hideDelete
                hideEdit
                hideShow
                link="logs"
              />
              <S.PaginationWrapper>
                <DefaultPagination count={pagedLogs.totalPages} page={page} color="primary"
                  showFirstButton showLastButton
                  onChange={(e, selectedPage) => { setPage(selectedPage); }} />
              </S.PaginationWrapper>
            </div>
          )}
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      <AuditLogDetailDialog
        log={selectedLog}
        t={t}
        companyTimezone={companyTimezone}
        companyLocale={companyLocale}
        companyName={selectedLog ? companyNameByKey(selectedLog.companyKey) : undefined}
        onClose={() => setSelectedLog(null)}
      />
    </Container>
  );
};
